/**
 * Checkpoint Service
 * 
 * Manages file transfer checkpoints for resume capability and recovery.
 * Handles both OPFS-based checkpoints and native filesystem checkpoints via extension.
 */

import { getFileFromOPFS, saveFileToOPFS } from '@/lib/file';
import type { FileTransferMetadata } from '@/lib/file-transfer-types';
import { extensionBridge } from './extension-bridge';

export interface FileTransferCheckpoint {
  transferId: string;
  fileHash: string;
  fileName: string;
  totalSize: number;
  chunkSize: number;
  totalChunks: number;
  completedChunks: number[];
  timestamp: number;
  peerId: string;
  direction: 'upload' | 'download';
  transferPath: 'opfs' | 'extension';
  metadata: Partial<FileTransferMetadata>;
  resumable: boolean;
  
  // Extension-specific fields
  nativeCheckpointPath?: string;
  memoryUsage?: number;
  networkMetrics?: {
    avgSpeed: number;
    rtt: number;
    jitter: number;
    reliability: number;
  };
  
  // Nonce recovery fields
  activeNonces?: Array<{
    nonce: number;
    chunkIndex: number;
    timestamp: number;
  }>;
  lastProcessedChunk?: number;
  sessionHash?: number;
}

export interface CheckpointStorageInfo {
  path: string;
  size: number;
  storageType: 'opfs' | 'extension';
  lastModified: number;
  isNative: boolean;
}

export interface ResumeResult {
  success: boolean;
  checkpoint: FileTransferCheckpoint;
  missingChunks: number[];
  verificationPassed: boolean;
  warnings?: string[];
}

/**
 * Service for managing file transfer checkpoints
 */
export class CheckpointService {
  private static readonly CHECKPOINT_PREFIX = 'checkpoint_';
  private static readonly OPFS_CHECKPOINT_DIR = 'checkpoints';
  private static readonly MAX_CHECKPOINT_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
  
  /**
   * Create a checkpoint for a file transfer
   */
  async createCheckpoint(
    transferId: string,
    fileHash: string,
    fileName: string,
    completedChunks: number[],
    metadata: {
      totalSize: number;
      chunkSize: number;
      totalChunks: number;
      peerId: string;
      direction: 'upload' | 'download';
      transferPath: 'opfs' | 'extension';
      networkMetrics?: any;
    }
  ): Promise<CheckpointStorageInfo> {
    
    const checkpoint: FileTransferCheckpoint = {
      transferId,
      fileHash,
      fileName,
      totalSize: metadata.totalSize,
      chunkSize: metadata.chunkSize,
      totalChunks: metadata.totalChunks,
      completedChunks: [...completedChunks].sort((a, b) => a - b),
      timestamp: Date.now(),
      peerId: metadata.peerId,
      direction: metadata.direction,
      transferPath: metadata.transferPath,
      metadata: {},
      resumable: true,
      networkMetrics: metadata.networkMetrics
    };

    console.log('[CheckpointService] Creating checkpoint', {
      transferId: transferId.slice(0, 8) + '...',
      fileName,
      completedChunks: completedChunks.length,
      totalChunks: metadata.totalChunks,
      progress: `${Math.round((completedChunks.length / metadata.totalChunks) * 100)}%`,
      transferPath: metadata.transferPath
    });

    if (metadata.transferPath === 'extension') {
      return await this.createNativeCheckpoint(checkpoint);
    } else {
      return await this.createOPFSCheckpoint(checkpoint);
    }
  }

  /**
   * Create checkpoint in OPFS for browser-only transfers
   */
  private async createOPFSCheckpoint(checkpoint: FileTransferCheckpoint): Promise<CheckpointStorageInfo> {
    const checkpointName = `${CheckpointService.CHECKPOINT_PREFIX}${checkpoint.transferId}.json`;
    const checkpointData = JSON.stringify(checkpoint, null, 2);
    const checkpointBlob = new Blob([checkpointData], { type: 'application/json' });
    const checkpointFile = new File([checkpointBlob], checkpointName);
    
    try {
      await saveFileToOPFS(checkpointFile, `${CheckpointService.OPFS_CHECKPOINT_DIR}/${checkpointName}`);
      
      return {
        path: `opfs://${CheckpointService.OPFS_CHECKPOINT_DIR}/${CheckpointService.CHECKPOINT_PREFIX}${checkpoint.transferId}.json`,
        size: new Blob([checkpointData]).size,
        storageType: 'opfs' as const,
        lastModified: Date.now(),
        isNative: false
      };
    } catch (error) {
      console.error('[CheckpointService] Failed to create OPFS checkpoint:', error);
      throw new Error(`Failed to create OPFS checkpoint: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create checkpoint in native filesystem via extension
   */
  private async createNativeCheckpoint(checkpoint: FileTransferCheckpoint): Promise<CheckpointStorageInfo> {
    // Use extension bridge if available for native checkpoint creation
    if (extensionBridge?.isExtensionAvailable) {
      try {
        const checkpointName = `${CheckpointService.CHECKPOINT_PREFIX}${checkpoint.transferId}.json`;
        const checkpointData = JSON.stringify(checkpoint, null, 2);
        
        // Use invokeExtensionWorker to create checkpoint via extension
        const result = await extensionBridge.invokeExtensionWorker('CREATE_CHECKPOINT', {
          transferId: checkpoint.transferId,
          checkpointData,
          metadata: {
            fileName: checkpoint.fileName,
            fileHash: checkpoint.fileHash,
            totalSize: checkpoint.totalSize,
            totalChunks: checkpoint.totalChunks,
            completedChunks: checkpoint.completedChunks
          }
        });
        
        if (result?.success) {
          console.log('[CheckpointService] Created native checkpoint via extension:', checkpointName);
          return {
            path: result.checkpointPath || `extension://${checkpoint.transferId}`,
            storageType: 'extension' as const,
            size: new Blob([checkpointData]).size,
            lastModified: Date.now(),
            isNative: true
          };
        } else {
          throw new Error(result?.error || 'Failed to create checkpoint in extension');
        }
      } catch (error) {
        console.warn('[CheckpointService] Failed to create native checkpoint, falling back to OPFS:', error);
        // Fall back to OPFS if extension fails
        const opfsInfo = await this.createOPFSCheckpoint(checkpoint);
        return {
          ...opfsInfo,
          storageType: 'opfs' as const
        };
      }
    } else {
      // Extension not available, use OPFS as fallback
      const opfsInfo = await this.createOPFSCheckpoint(checkpoint);
      return {
        ...opfsInfo,
        storageType: 'opfs' as const
      };
    }
  }

  /**
   * Resume transfer from checkpoint
   */
  async resumeFromCheckpoint(checkpointPath: string): Promise<ResumeResult> {
    console.log('[CheckpointService] Resuming from checkpoint:', checkpointPath);
    
    try {
      const checkpoint = await this.loadCheckpoint(checkpointPath);
      
      if (!checkpoint.resumable) {
        return {
          success: false,
          checkpoint,
          missingChunks: [],
          verificationPassed: false,
          warnings: ['Checkpoint marked as non-resumable']
        };
      }

      // Calculate missing chunks
      const allChunks = Array.from({ length: checkpoint.totalChunks }, (_, i) => i);
      const missingChunks = allChunks.filter(chunk => !checkpoint.completedChunks.includes(chunk));
      
      // Verify checkpoint integrity
      const verificationResult = await this.verifyCheckpoint(checkpoint);
      
      console.log('[CheckpointService] Resume analysis', {
        transferId: checkpoint.transferId.slice(0, 8) + '...',
        completedChunks: checkpoint.completedChunks.length,
        missingChunks: missingChunks.length,
        verificationPassed: verificationResult.passed,
        age: Date.now() - checkpoint.timestamp
      });

      return {
        success: verificationResult.passed,
        checkpoint,
        missingChunks,
        verificationPassed: verificationResult.passed,
        warnings: verificationResult.warnings
      };
      
    } catch (error) {
      console.error('[CheckpointService] Failed to resume from checkpoint:', error);
      throw new Error(`Failed to resume from checkpoint: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Load checkpoint data from storage
   */
  private async loadCheckpoint(checkpointPath: string): Promise<FileTransferCheckpoint> {
    if (checkpointPath.startsWith('opfs://')) {
      return await this.loadOPFSCheckpoint(checkpointPath);
    } else {
      return await this.loadNativeCheckpoint(checkpointPath);
    }
  }

  /**
   * Load checkpoint from OPFS
   */
  private async loadOPFSCheckpoint(checkpointPath: string): Promise<FileTransferCheckpoint> {
    const opfsPath = checkpointPath.replace('opfs://', '');
    const checkpointFile = await getFileFromOPFS(opfsPath);
    
    if (!checkpointFile) {
      throw new Error(`Checkpoint not found: ${checkpointPath}`);
    }

    const checkpointText = await checkpointFile.text();
    return JSON.parse(checkpointText) as FileTransferCheckpoint;
  }

  /**
   * Load checkpoint from native filesystem (via extension)
   */
  private async loadNativeCheckpoint(checkpointPath: string): Promise<FileTransferCheckpoint> {
    const transferId = this.extractTransferIdFromPath(checkpointPath);
    
    // Try to load from extension first
    if (extensionBridge?.isExtensionAvailable) {
      try {
        const result = await extensionBridge.invokeExtensionWorker('RESTORE_CHECKPOINT', {
          transferId,
          checkpointPath: checkpointPath
        });
        
        if (result?.success && result.checkpointData) {
          console.log('[CheckpointService] Loaded native checkpoint from extension');
          return result.checkpointData as FileTransferCheckpoint;
        }
      } catch (error) {
        console.warn('[CheckpointService] Failed to load native checkpoint from extension:', error);
      }
    }
    
    // Fall back to OPFS checkpoint
    const opfsPath = `opfs://${CheckpointService.OPFS_CHECKPOINT_DIR}/${CheckpointService.CHECKPOINT_PREFIX}${transferId}.json`;
    return await this.loadOPFSCheckpoint(opfsPath);
  }

  /**
   * Verify checkpoint integrity and age
   */
  private async verifyCheckpoint(checkpoint: FileTransferCheckpoint): Promise<{
    passed: boolean;
    warnings: string[];
  }> {
    const warnings: string[] = [];
    
    // Check checkpoint age
    const age = Date.now() - checkpoint.timestamp;
    if (age > CheckpointService.MAX_CHECKPOINT_AGE) {
      warnings.push(`Checkpoint is ${Math.round(age / (24 * 60 * 60 * 1000))} days old`);
    }

    // Verify basic structure
    if (!checkpoint.transferId || !checkpoint.fileHash || checkpoint.totalChunks <= 0) {
      return { passed: false, warnings: ['Checkpoint structure is invalid'] };
    }

    // Verify completed chunks are within valid range
    const invalidChunks = checkpoint.completedChunks.filter(
      (chunk: any) => chunk >= checkpoint.totalChunks
    );
    
    if (invalidChunks.length > 0) {
      warnings.push(`${invalidChunks.length} invalid chunk indices found`);
    }

    // Check for reasonable progress
    const progress = checkpoint.completedChunks.length / checkpoint.totalChunks;
    if (progress === 0) {
      warnings.push('No progress recorded in checkpoint');
    }

    return {
      passed: invalidChunks.length === 0,
      warnings
    };
  }

  /**
   * List all available checkpoints
   */
  async listCheckpoints(): Promise<{
    transferId: string;
    fileName: string;
    progress: number;
    age: number;
    transferPath: 'opfs' | 'extension';
    resumable: boolean;
    path: string;
  }[]> {
    
    const checkpoints: any[] = [];
    
    try {
      // Get OPFS checkpoints
      const opfsCheckpoints = await this.listOPFSCheckpoints();
      checkpoints.push(...opfsCheckpoints);
      
      // In full implementation, would also list native checkpoints via extension
      
    } catch (error) {
      console.warn('[CheckpointService] Error listing checkpoints:', error);
    }

    return checkpoints.sort((a, b) => b.age - a.age); // Newest first
  }

  /**
   * List OPFS checkpoints
   */
  private async listOPFSCheckpoints(): Promise<any[]> {
    const checkpoints: any[] = [];
    
    try {
      // Import required OPFS functionality
      const { getOriginPrivateDirectory } = await import('native-file-system-adapter');
      const root = await getOriginPrivateDirectory();
      
      // Try to get the checkpoint directory
      let checkpointDir;
      try {
        checkpointDir = await root.getDirectoryHandle(CheckpointService.OPFS_CHECKPOINT_DIR, { create: false });
      } catch (error) {
        // Directory doesn't exist yet - no checkpoints
        console.log('[CheckpointService] No checkpoint directory found in OPFS');
        return [];
      }
      
      // Iterate through checkpoint files
      // @ts-ignore - TypeScript may not recognize the async iterator
      for await (const entry of checkpointDir.values()) {
        if (entry.kind === 'file' && entry.name.startsWith(CheckpointService.CHECKPOINT_PREFIX)) {
          try {
            // Read checkpoint file
            const fileHandle = await checkpointDir.getFileHandle(entry.name);
            const file = await fileHandle.getFile();
            const checkpointText = await file.text();
            const checkpoint = JSON.parse(checkpointText) as FileTransferCheckpoint;
            
            // Calculate age and progress
            const age = Date.now() - checkpoint.timestamp;
            const totalChunks = checkpoint.metadata.totalChunks || 1; // Default to 1 to avoid division by zero
            const progress = (checkpoint.completedChunks.length / totalChunks) * 100;
            
            // Extract transfer ID from filename
            const transferId = entry.name
              .replace(CheckpointService.CHECKPOINT_PREFIX, '')
              .replace('.json', '');
            
            // Get fileName safely from metadata
            const fileName = (checkpoint.metadata as any).fileName || 'unknown';
            
            checkpoints.push({
              transferId,
              fileName,
              progress,
              age,
              transferPath: 'opfs' as const,
              resumable: checkpoint.completedChunks.length < totalChunks,
              path: `opfs://${CheckpointService.OPFS_CHECKPOINT_DIR}/${entry.name}`
            });
            
          } catch (error) {
            console.warn(`[CheckpointService] Failed to read checkpoint ${entry.name}:`, error);
          }
        }
      }
      
      console.log(`[CheckpointService] Found ${checkpoints.length} OPFS checkpoints`);
      
    } catch (error) {
      console.error('[CheckpointService] Error listing OPFS checkpoints:', error);
    }
    
    return checkpoints;
  }

  /**
   * Clean up old checkpoints
   */
  async cleanupOldCheckpoints(maxAge: number = CheckpointService.MAX_CHECKPOINT_AGE): Promise<{
    cleaned: number;
    errors: string[];
  }> {
    
    console.log('[CheckpointService] Cleaning up old checkpoints', {
      maxAge: Math.round(maxAge / (24 * 60 * 60 * 1000)) + ' days'
    });

    let cleaned = 0;
    const errors: string[] = [];

    try {
      const checkpoints = await this.listCheckpoints();
      const oldCheckpoints = checkpoints.filter(cp => cp.age > maxAge);
      
      for (const checkpoint of oldCheckpoints) {
        try {
          await this.deleteCheckpoint(checkpoint.path);
          cleaned++;
        } catch (error) {
          errors.push(`Failed to delete ${checkpoint.path}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
      
    } catch (error) {
      errors.push(`Failed to list checkpoints: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    console.log('[CheckpointService] Cleanup completed', { cleaned, errors: errors.length });
    
    return { cleaned, errors };
  }

  /**
   * Delete a specific checkpoint
   */
  async deleteCheckpoint(checkpointPath: string): Promise<void> {
    console.log('[CheckpointService] Deleting checkpoint:', checkpointPath);
    
    if (checkpointPath.startsWith('opfs://')) {
      // Delete OPFS checkpoint
      const opfsPath = checkpointPath.replace('opfs://', '');
      
      try {
        const { getOriginPrivateDirectory } = await import('native-file-system-adapter');
        const root = await getOriginPrivateDirectory();
        
        // Parse the path to get directory and filename
        const pathParts = opfsPath.split('/');
        const fileName = pathParts.pop();
        const dirPath = pathParts.join('/');
        
        if (dirPath === CheckpointService.OPFS_CHECKPOINT_DIR && fileName) {
          const checkpointDir = await root.getDirectoryHandle(CheckpointService.OPFS_CHECKPOINT_DIR);
          await checkpointDir.removeEntry(fileName);
          console.log('[CheckpointService] Deleted OPFS checkpoint:', fileName);
        }
      } catch (error) {
        console.error('[CheckpointService] Failed to delete OPFS checkpoint:', error);
        throw error;
      }
    } else {
      // Delete native checkpoint via extension
      if (extensionBridge?.isExtensionAvailable) {
        try {
          const transferId = this.extractTransferIdFromPath(checkpointPath);
          const result = await extensionBridge.invokeExtensionWorker('CLEANUP', {
            transferId,
            includeCheckpoints: true
          });
          
          if (result?.success) {
            console.log('[CheckpointService] Deleted native checkpoint via extension');
          } else {
            throw new Error(result?.error || 'Failed to delete checkpoint');
          }
        } catch (error) {
          console.error('[CheckpointService] Failed to delete native checkpoint:', error);
          throw error;
        }
      } else {
        console.warn('[CheckpointService] Extension not available for native checkpoint deletion');
      }
    }
  }

  /**
   * Update checkpoint with new progress
   */
  async updateCheckpoint(
    transferId: string,
    newCompletedChunks: number[],
    networkMetrics?: any
  ): Promise<void> {
    
    console.log('[CheckpointService] Updating checkpoint', {
      transferId: transferId.slice(0, 8) + '...',
      newChunks: newCompletedChunks.length
    });

    // Load existing checkpoint
    const checkpointPath = `opfs://${CheckpointService.OPFS_CHECKPOINT_DIR}/${CheckpointService.CHECKPOINT_PREFIX}${transferId}.json`;
    
    try {
      const checkpoint = await this.loadCheckpoint(checkpointPath);
      
      // Merge completed chunks
      const allCompleted = new Set([...checkpoint.completedChunks, ...newCompletedChunks]);
      checkpoint.completedChunks = Array.from(allCompleted).sort((a, b) => a - b);
      checkpoint.timestamp = Date.now();
      
      if (networkMetrics) {
        checkpoint.networkMetrics = networkMetrics;
      }

      // Save updated checkpoint
      if (checkpoint.transferPath === 'extension') {
        await this.createNativeCheckpoint(checkpoint);
      } else {
        await this.createOPFSCheckpoint(checkpoint);
      }
      
    } catch (error) {
      console.warn('[CheckpointService] Failed to update checkpoint (non-critical):', error);
    }
  }

  /**
   * Extract transfer ID from checkpoint path
   */
  private extractTransferIdFromPath(path: string): string {
    const match = path.match(/checkpoint_([^.]+)/);
    return match ? match[1] : '';
  }

  /**
   * Get checkpoint statistics
   */
  async getCheckpointStats(): Promise<{
    totalCheckpoints: number;
    totalSize: number;
    oldestCheckpoint: number;
    newestCheckpoint: number;
    resumableCount: number;
  }> {
    
    const checkpoints = await this.listCheckpoints();
    
    if (checkpoints.length === 0) {
      return {
        totalCheckpoints: 0,
        totalSize: 0,
        oldestCheckpoint: 0,
        newestCheckpoint: 0,
        resumableCount: 0
      };
    }

    return {
      totalCheckpoints: checkpoints.length,
      totalSize: 0, // Would calculate actual sizes
      oldestCheckpoint: Math.max(...checkpoints.map(cp => cp.age)),
      newestCheckpoint: Math.min(...checkpoints.map(cp => cp.age)),
      resumableCount: checkpoints.filter(cp => cp.resumable).length
    };
  }
}

// Singleton instance
export const checkpointService = new CheckpointService();
